import { useState, useEffect, useRef } from 'react';
import syncService from '../services/syncService';

/**
 * Drop-in replacement for `useState` + "persist to localStorage" for a value
 * that is mirrored to the cloud (see syncService).
 *
 * `hydrate(stored)` turns the raw stored value (or null when nothing is
 * stored) into state. It is used for the initial value and again whenever a
 * newer value arrives from another computer, so validation lives in one place.
 *
 * Nothing is written on mount, so a fresh computer never uploads defaults
 * over a real cloud copy; only genuine changes are saved and marked dirty.
 */
export default function useSyncedStorage(key, hydrate) {
  const [value, setValue] = useState(() => hydrate(syncService.readLocal(key)));
  const lastRef = useRef(value);
  const hydrateRef = useRef(hydrate);
  hydrateRef.current = hydrate;

  useEffect(() => {
    if (value === lastRef.current) return; // initial value, or just applied from the cloud
    lastRef.current = value;
    syncService.writeLocal(key, value);
    syncService.markDirty(key);
  }, [key, value]);

  useEffect(() => syncService.onApplied((changedKey, stored) => {
    if (changedKey !== key) return;
    const next = hydrateRef.current(stored);
    lastRef.current = next; // so the effect above does not treat it as a local edit
    setValue(next);
  }), [key]);

  return [value, setValue];
}
